// ══════════════════════════════════════════════════════════════
//  TablePanel — Raumbuch Spreadsheet
//  Design: Tabellenansicht Handoff (Claude Design)
//  20 Spalten, 5 Gruppen, Inline-Edit, Filter, Row-Selection
// ══════════════════════════════════════════════════════════════

import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import type { TableColumn, GroupId, RaumbuchRow } from './tableColumns';
import { GROUP_META, STATUS_STYLES } from './tableColumns';
import '../../styles/raumbuch.css';

// ── SVG Icons (inline, matching design) ──────────────────────

const ScanIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="2.2"/><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1"/>
  </svg>
);
const CubeIcon = () => (
  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2 3 7v10l9 5 9-5V7z"/><path d="M3 7l9 5 9-5M12 12v10"/>
  </svg>
);
const BoltIcon = () => (
  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2L4 14h7l-1 8 9-12h-7z"/>
  </svg>
);
const ClockIcon = () => (
  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/>
  </svg>
);
const ChevronDown = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);
const PlusIcon = ({ size = 12 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);
const XIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const SortAscIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
    <line x1="3" y1="6" x2="21" y2="6"/><line x1="6" y1="12" x2="18" y2="12"/><line x1="9" y1="18" x2="15" y2="18"/>
  </svg>
);
const SortDescIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
    <line x1="3" y1="18" x2="21" y2="18"/><line x1="6" y1="12" x2="18" y2="12"/><line x1="9" y1="6" x2="15" y2="6"/>
  </svg>
);

const GROUP_ICONS: Record<string, React.ReactNode> = {
  scan: <ScanIcon />,
  cube: <CubeIcon />,
  bolt: <BoltIcon />,
  clock: <ClockIcon />,
};

const TYPE_ICONS: Record<string, React.ReactNode> = {
  text: <span style={{ fontWeight: 600, fontSize: 10 }}>T</span>,
  number: <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10 }}>#</span>,
  date: (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  ),
  status: (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <circle cx="12" cy="12" r="9"/>
    </svg>
  ),
  user: (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 4-6 8-6s8 2 8 6"/>
    </svg>
  ),
};

// ── Props ────────────────────────────────────────────────────

interface TablePanelProps {
  columns: TableColumn[];
  rows: RaumbuchRow[];
  selectedRows: Set<number>;
  onToggleRow: (idx: number) => void;
  onCellChange: (rowId: number, key: string, value: string) => void;
  onRowClick?: (rowId: number) => void;
  hoverIdx?: number | null;
  onHoverIdx?: (idx: number | null) => void;
}

// ── HeaderCell ───────────────────────────────────────────────

function HeaderCell({ col, height, onFilter, onSort }: {
  col: TableColumn;
  height: number;
  onFilter: (value: string) => void;
  onSort: (dir: 'asc' | 'desc') => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [filterVal, setFilterVal] = useState('');
  const isScan = col.source === 'scan';

  return (
    <div
      className={`rb-head-cell ${isScan ? 'rb-head-cell--scan' : ''}`}
      style={{ width: col.width, height }}
    >
      <div className={`rb-head-cell__type ${isScan ? 'rb-head-cell__type--scan' : ''}`}>
        {isScan ? <ScanIcon /> : TYPE_ICONS[col.type]}
      </div>
      <div className={`rb-head-cell__label ${isScan ? 'rb-head-cell__label--scan' : ''}`}>
        {col.label}
      </div>
      <button
        className={`rb-head-cell__action ${menuOpen ? 'rb-head-cell__action--open' : ''}`}
        onClick={() => setMenuOpen(o => !o)}
      >
        <ChevronDown />
      </button>
      {menuOpen && (
        <div className="rb-head-menu" onClick={e => e.stopPropagation()}>
          <div className="rb-head-menu__label">Filter</div>
          <input
            autoFocus
            type="text"
            value={filterVal}
            onChange={e => setFilterVal(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && filterVal) {
                onFilter(filterVal);
                setMenuOpen(false);
                setFilterVal('');
              }
            }}
            placeholder="enthält…"
            className="rb-head-menu__input"
          />
          <div className="rb-head-menu__divider" />
          <button className="rb-head-menu__item" onClick={() => { onSort('asc'); setMenuOpen(false); }}>
            <SortAscIcon /> Aufsteigend sortieren
          </button>
          <button className="rb-head-menu__item" onClick={() => { onSort('desc'); setMenuOpen(false); }}>
            <SortDescIcon /> Absteigend sortieren
          </button>
          <button className="rb-head-menu__item" onClick={() => setMenuOpen(false)}>
            Spalte ausblenden
          </button>
        </div>
      )}
    </div>
  );
}

// ── Cell ─────────────────────────────────────────────────────

function Cell({ col, value, height, selected, editing, onSelect, onDoubleClick, onCommit, onCancel }: {
  col: TableColumn;
  value: string | number | null;
  height: number;
  selected: boolean;
  editing: boolean;
  onSelect: () => void;
  onDoubleClick: () => void;
  onCommit: (val: string) => void;
  onCancel: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const isScan = col.source === 'scan';

  const renderValue = () => {
    if (value == null || value === '') return null;
    const v = String(value);

    if (col.type === 'status') {
      const s = STATUS_STYLES[v] || { bg: '#f3f4f6', fg: '#6b7280', dot: '#9ca3af' };
      return (
        <span className="rb-status" style={{ background: s.bg, color: s.fg }}>
          <span className="rb-status__dot" style={{ background: s.dot }} />
          {v}
        </span>
      );
    }
    if (col.type === 'user') {
      const initials = v.split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase();
      return (
        <span className="rb-user">
          <span className="rb-user__avatar">{initials}</span>
          <span className="rb-user__name">{v}</span>
        </span>
      );
    }
    if (col.type === 'number') {
      return <span className={`rb-val-number ${isScan ? 'rb-val-number--scan' : ''}`}>{v}</span>;
    }
    if (col.type === 'date') {
      return <span className="rb-val-date">{v}</span>;
    }
    if (isScan) {
      return <span className="rb-val-scan-text">{v}</span>;
    }
    return <span className="rb-val-text">{v}</span>;
  };

  const cellClass = [
    'rb-cell',
    isScan ? 'rb-cell--scan' : '',
    selected ? 'rb-cell--selected' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={cellClass}
      style={{ width: col.width, height }}
      title={isScan ? 'Aus Punktwolke gemessen — nicht editierbar' : undefined}
      onClick={onSelect}
      onDoubleClick={onDoubleClick}
    >
      {editing ? (
        <input
          ref={inputRef}
          defaultValue={value ?? ''}
          onBlur={e => onCommit(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') onCommit((e.target as HTMLInputElement).value);
            if (e.key === 'Escape') onCancel();
          }}
          className="rb-cell__input"
        />
      ) : renderValue()}
    </div>
  );
}

// ── TablePanel (main export) ─────────────────────────────────

export default function TablePanel({
  columns,
  rows,
  selectedRows,
  onToggleRow,
  onCellChange,
  onRowClick,
  hoverIdx,
  onHoverIdx,
}: TablePanelProps) {
  const [selCell, setSelCell] = useState<{ r: number; c: number } | null>(null);
  const [editingCell, setEditingCell] = useState<{ r: number; c: number } | null>(null);
  const [filters, setFilters] = useState<{ col: string; value: string }[]>([]);
  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const GUTTER_W = 40;
  const IDX_W = 32;
  const ROW_H = 30; // compact density
  const HEAD_H = 36;

  // ── Filtering ──
  const filteredRows = useMemo(() => {
    let result = rows;
    if (filters.length > 0) {
      result = result.filter(r =>
        filters.every(f => {
          const v = String(r.values[f.col] ?? '').toLowerCase();
          return v.includes(f.value.toLowerCase());
        })
      );
    }
    if (sortCol) {
      result = [...result].sort((a, b) => {
        const av = String(a.values[sortCol] ?? '');
        const bv = String(b.values[sortCol] ?? '');
        const cmp = av.localeCompare(bv, 'de', { numeric: true });
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return result;
  }, [rows, filters, sortCol, sortDir]);

  const emptyRowCount = Math.max(0, 24 - filteredRows.length);

  // ── Inline edit ──
  const startEdit = useCallback((r: number, c: number) => {
    if (columns[c].readonly) return;
    setSelCell({ r, c });
    setEditingCell({ r, c });
  }, [columns]);

  const commitEdit = useCallback((val: string) => {
    if (!editingCell) return;
    const row = filteredRows[editingCell.r];
    const col = columns[editingCell.c];
    if (row && col) onCellChange(row.id, col.key, val);
    setEditingCell(null);
  }, [editingCell, filteredRows, columns, onCellChange]);

  // ── Group band computation ──
  // With box-sizing: border-box on all .rb-panel descendants, every cell
  // occupies exactly its `width` (padding + border included).
  // So the group band width = plain sum of child column widths.
  const groups = useMemo(() => {
    const result: { key: GroupId; width: number }[] = [];
    for (const col of columns) {
      const g = col.group;
      const last = result[result.length - 1];
      if (last && last.key === g) last.width += col.width;
      else result.push({ key: g, width: col.width });
    }
    return result;
  }, [columns]);

  return (
    <div className="rb-panel">
      {/* Filter chips */}
      {filters.length > 0 && (
        <div className="rb-filter-bar">
          <span className="rb-filter-label">Filter:</span>
          {filters.map((f, i) => (
            <span key={i} className="rb-filter-chip">
              <span style={{ color: '#6b7280' }}>{columns.find(c => c.key === f.col)?.label || f.col}</span>
              <span style={{ color: '#d1d5db' }}>=</span>
              <span>{f.value}</span>
              <button className="rb-filter-chip__x" onClick={() => setFilters(fs => fs.filter((_, j) => j !== i))}>
                <XIcon />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Scrollable table body */}
      <div className="rb-scroll">
        <div style={{ position: 'relative', minWidth: 'max-content' }}>
          {/* Group band */}
          <div className="rb-group-band">
            <div className="rb-group-band__spacer" style={{ width: GUTTER_W + IDX_W }} />
            {groups.map((g, i) => {
              const meta = GROUP_META[g.key];
              return (
                <div
                  key={i}
                  className="rb-group-band__item"
                  style={{ width: g.width, color: meta.color, background: meta.bg }}
                >
                  {meta.icon && (
                    <span className="rb-group-band__icon">
                      {GROUP_ICONS[meta.icon]}
                    </span>
                  )}
                  {meta.label}
                </div>
              );
            })}
          </div>

          {/* Header row */}
          <div className="rb-head-row" style={{ height: HEAD_H, top: 22 }}>
            <div className="rb-gutter" style={{ width: GUTTER_W, height: HEAD_H }} />
            <div className="rb-idx-cell" style={{ width: IDX_W, height: HEAD_H, color: '#d1d5db' }}>#</div>
            {columns.map(col => (
              <HeaderCell
                key={col.key}
                col={col}
                height={HEAD_H}
                onFilter={value => setFilters(fs => [...fs.filter(f => f.col !== col.key), { col: col.key, value }])}
                onSort={dir => { setSortCol(col.key); setSortDir(dir); }}
              />
            ))}
            <div className="rb-add-col">
              <PlusIcon />
            </div>
          </div>

          {/* Data rows */}
          {filteredRows.map((row, ri) => {
            const isHovered = hoverIdx === ri;
            return (
              <div
                key={row.id}
                className={`rb-row${isHovered ? ' rb-row--hover' : ''}${selectedRows.has(ri) ? ' rb-row--selected' : ''}`}
                style={{ height: ROW_H }}
                onMouseEnter={() => onHoverIdx?.(ri)}
                onMouseLeave={() => onHoverIdx?.(null)}
                onClick={() => onRowClick?.(row.id)}
              >
                <div className="rb-gutter" style={{ width: GUTTER_W, height: ROW_H }}>
                  <button
                    className={`rb-row-sel ${selectedRows.has(ri) ? 'rb-row-sel--on' : ''}`}
                    onClick={e => { e.stopPropagation(); onToggleRow(ri); }}
                  />
                </div>
                <div className="rb-idx-cell" style={{ width: IDX_W, height: ROW_H }}>
                  {ri + 1}
                </div>
                {columns.map((col, ci) => {
                  const isSel = selCell?.r === ri && selCell?.c === ci;
                  const isEdit = editingCell?.r === ri && editingCell?.c === ci;
                  return (
                    <Cell
                      key={col.key}
                      col={col}
                      value={row.values[col.key]}
                      height={ROW_H}
                      selected={isSel}
                      editing={isEdit}
                      onSelect={() => setSelCell({ r: ri, c: ci })}
                      onDoubleClick={() => startEdit(ri, ci)}
                      onCommit={commitEdit}
                      onCancel={() => setEditingCell(null)}
                    />
                  );
                })}
                <div className="rb-cell__trail" style={{ height: ROW_H }} />
              </div>
            );
          })}

          {/* Empty padding rows */}
          {Array.from({ length: emptyRowCount }).map((_, i) => (
            <div key={`e${i}`} className="rb-row" style={{ height: ROW_H }}>
              <div className="rb-gutter" style={{ width: GUTTER_W, height: ROW_H }} />
              <div className="rb-idx-cell" style={{ width: IDX_W, height: ROW_H, color: '#e5e7eb' }}>
                {filteredRows.length + i + 1}
              </div>
              {columns.map(col => (
                <div key={col.key} className="rb-cell--empty" style={{ width: col.width, height: ROW_H }} />
              ))}
              <div className="rb-cell__trail" style={{ height: ROW_H }} />
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="rb-footer">
        <span className="rb-footer__item">
          {filteredRows.length} {filteredRows.length === 1 ? 'Zeile' : 'Zeilen'}
          {filters.length > 0 && <span style={{ color: '#9ca3af' }}>&nbsp;von {rows.length}</span>}
        </span>
        {selectedRows.size > 0 && (
          <span className="rb-footer__item rb-footer__item--sel">
            {selectedRows.size} ausgewählt
          </span>
        )}
        <span className="rb-footer__item rb-footer__item--right">
          {columns.length} Spalten
        </span>
      </div>
    </div>
  );
}
